/**
 * Campaign Extraction Frontend Example
 * Usage: Include this JS and a minimal HTML/CSS to test campaign extraction locally.
 */

import React, { useState, useEffect } from 'react';
import './campaigns.css';

const TABLE_HEADERS = [
  'store', 'campaign_date_start', 'campaign_date_end', 'product_name', 'brand', 'category', 'amount', 'price', 'kampanjpris', 'klubbpris', 'jfr_pris', 'lowest_30_days', 'description'
];

const backendUrl = process.env.REACT_APP_RAILWAY_URL || 'http://localhost:8000';

// Staff Login Component
function StaffLogin({ onLoginSuccess }) {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [apiError, setApiError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
    setApiError('');
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    setErrors(validationErrors);
    
    if (Object.keys(validationErrors).length === 0) {
      setIsSubmitting(true);
      setApiError('');
      
      try {
        const response = await fetch(`${backendUrl}/api/staff-login/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Login failed');
        }

        // Store user data and login state
        localStorage.setItem('staffUser', JSON.stringify(data.user));
        localStorage.setItem('isStaffLoggedIn', 'true');
        
        setLoginSuccess(true);
        
        // Call success callback after short delay
        setTimeout(() => {
          onLoginSuccess(data.user);
        }, 1000);
      } catch (error) {
        setApiError(error.message);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="campaigns-container">
      <div className="campaigns-card">
        <div className="login-header">
          <h1>Staff Access Required</h1>
          <p>Please sign in with your staff or administrator account to access.</p>
        </div>
        
        {loginSuccess ? (
          <div className="login-success">
            <div className="success-icon">✓</div>
            <p>Login successful! Loading campaign tools...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="staff-login-form">
            {apiError && <div className="campaigns-error">{apiError}</div>}
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'input-error' : ''}
                placeholder="Enter your staff email"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={errors.password ? 'input-error' : ''}
                placeholder="Enter your password"
              />
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>
            
            <button 
              type="submit" 
              className="staff-login-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </button>
            
            <div className="login-note">
              <p><strong>Note:</strong> Only staff and administrator accounts can access this page.</p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// Main Campaigns Component
function CampaignsPage() {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [editedProducts, setEditedProducts] = useState([]);
  const [saveStatus, setSaveStatus] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [canRetry, setCanRetry] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [manualProducts, setManualProducts] = useState([]);

  // Check for existing authentication on component mount
  useEffect(() => {
    const savedUser = localStorage.getItem('staffUser');
    const isLoggedIn = localStorage.getItem('isStaffLoggedIn');
    
    if (savedUser && isLoggedIn === 'true') {
      setUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
    }
  }, []);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('staffUser');
    localStorage.removeItem('isStaffLoggedIn');
    setUser(null);
    setIsAuthenticated(false);
    // Reset all campaign state
    setSelectedFiles([]);
    setResult(null);
    setEditedProducts([]);
    setError(null);
    setSaveStatus(null);
    setSaveMessage('');
    setRetryCount(0);
    setCanRetry(false);
    setManualMode(false);
    setManualProducts([]);
  };

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files));
    setError(null);
    setResult(null);
    setEditedProducts([]);
    setSaveStatus(null);
    setSaveMessage('');
    setRetryCount(0);
    setCanRetry(false);
    setManualMode(false);
    setManualProducts([]);
  };

  const handleFileDelete = (indexToDelete) => {
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToDelete));
  };

  const createEmptyProduct = () => {
    const emptyProduct = {};
    TABLE_HEADERS.forEach(header => {
      emptyProduct[header] = '';
    });
    return emptyProduct;
  };

  const handleManualMode = () => {
    setManualMode(true);
    setError(null);
    setResult(null);
    setEditedProducts([]);
    setCanRetry(false);
    
    // Create initial empty rows
    const initialProducts = Array(3).fill(null).map(() => createEmptyProduct());
    setManualProducts(initialProducts);
  };

  const handleAddRow = () => {
    setManualProducts(prev => [...prev, createEmptyProduct()]);
  };

  const handleRemoveRow = (indexToRemove) => {
    if (manualProducts.length > 1) {
      setManualProducts(prev => prev.filter((_, index) => index !== indexToRemove));
    }
  };

  const handleManualCellChange = (rowIdx, key, value) => {
    setManualProducts(prev => prev.map((prod, idx) =>
      idx === rowIdx ? { ...prod, [key]: value } : prod
    ));
  };

  const handleSaveManual = async () => {
    // Filter out completely empty rows
    const validProducts = manualProducts.filter(product => {
      return Object.values(product).some(value => value && value.trim() !== '');
    });

    if (validProducts.length === 0) {
      setError('Please enter at least one product with some data.');
      return;
    }

    // Check if required fields are filled
    const missingRequired = validProducts.some(product => 
      !product.product_name || !product.product_name.trim()
    );

    if (missingRequired) {
      setError('Please ensure all products have at least a product name.');
      return;
    }

    setSaveStatus('saving');
    setError(null);
    setSaveMessage('');
    
    try {
      const response = await fetch(`${backendUrl}/api/campaigns/save/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ products: validProducts })
      });
      const data = await response.json();
      if (data.success) {
        setSaveStatus('success');
        setSaveMessage(data.message || 'Manual data saved successfully!');
      } else {
        setSaveStatus('error');
        setError(data.error || 'Failed to save manual data.');
      }
    } catch (err) {
      setSaveStatus('error');
      setError('Network or server error.');
    }
  };

  const handleExitManual = () => {
    setManualMode(false);
    setManualProducts([]);
    setError(null);
    setSaveStatus(null);
    setSaveMessage('');
  };

  const getErrorMessage = (error, errorType) => {
    switch (errorType) {
      case 'api_overload':
        return '🔄 The AI service is currently busy due to high demand. This usually resolves in a few minutes.';
      case 'timeout':
        return '⏰ Request timeout. The AI service is experiencing delays. Please try manual entry or wait a few minutes.';
      case 'extraction_failed':
        return '⚠️ Failed to extract campaign data. Please check your file format and try again.';
      case 'processing_error':
        return '❌ There was an error processing your files. Please try again.';
      case 'max_retries_exceeded':
        return '⏰ Maximum retry attempts reached. The service is experiencing high traffic. Please wait a few minutes and try again.';
      case 'network_error':
        return '🌐 Network connection error. Please check your internet connection and try again.';
      default:
        return error || '❌ An unexpected error occurred. Please try again.';
    }
  };

  const performExtraction = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setEditedProducts([]);
    setSaveStatus(null);
    setSaveMessage('');
    setCanRetry(false);
    
    try {
      const formData = new FormData();
      selectedFiles.forEach(file => formData.append('files', file));
      
      // Add timeout to the request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 35000); // 35 second timeout
      
      const response = await fetch(`${backendUrl}/api/campaigns/`, {
        method: 'POST',
        body: formData,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      const data = await response.json();
      
      if (data.success) {
        setResult(data.data);
        setRetryCount(0);
        // Initialize editedProducts with the extracted products
        setEditedProducts(
          (data.data.products || []).map(prod => ({
            ...prod,
            store: data.data.store || '',
            campaign_date_start: data.data.campaign_date_start || '',
            campaign_date_end: data.data.campaign_date_end || ''
          }))
        );
      } else {
        const errorMessage = getErrorMessage(data.error, data.error_type);
        setError(errorMessage);
        setCanRetry(data.retry_suggested || data.error_type === 'api_overload' || data.error_type === 'timeout');
      }
    } catch (err) {
      console.error('Network error:', err);
      if (err.name === 'AbortError') {
        setError(getErrorMessage('Request timed out. The AI service is taking too long to respond. Please try manual entry or wait a few minutes.', 'timeout'));
      } else {
        setError(getErrorMessage('Network connection failed. Please check your internet connection.', 'network_error'));
      }
      setCanRetry(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFiles.length) {
      setError('Please select at least one file.');
      return;
    }
    
    setRetryCount(prev => prev + 1);
    await performExtraction();
  };

  const handleRetryExtraction = async () => {
    setRetryCount(prev => prev + 1);
    await performExtraction();
  };

  const handleCellChange = (rowIdx, key, value) => {
    setEditedProducts(prev => prev.map((prod, idx) =>
      idx === rowIdx ? { ...prod, [key]: value } : prod
    ));
  };

  const handleSave = async () => {
    setSaveStatus('saving');
    setError(null);
    setSaveMessage('');
    try {
      const response = await fetch(`${backendUrl}/api/campaigns/save/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ products: editedProducts })
      });
      const data = await response.json();
      if (data.success) {
        setSaveStatus('success');
        setSaveMessage(data.message || 'Changes saved successfully!');
      } else {
        setSaveStatus('error');
        setError(data.error || 'Failed to save changes.');
      }
    } catch (err) {
      setSaveStatus('error');
      setError('Network or server error.');
    }
  };

  const handleRetry = () => {
    // Reset all state completely
    setSelectedFiles([]);
    setResult(null);
    setEditedProducts([]);
    setError(null);
    setSaveStatus(null);
    setSaveMessage('');
    setRetryCount(0);
    setCanRetry(false);
    setManualMode(false);
    setManualProducts([]);
    
    // Reset the file input by clearing its value
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return <StaffLogin onLoginSuccess={handleLoginSuccess} />;
  }

  // Show campaign interface if authenticated
  return (
    <div className="campaigns-container">
      <div className="campaigns-card">
        <div className="campaign-header">
          <div className="header-content">
            <h1>Hello, {user?.full_name || user?.username || 'Staff'}</h1>
            <div className="user-info">
              <div className="user-badges">
                {user?.is_superuser && <span className="user-badge superuser">Superuser</span>}
                {user?.is_staff && !user?.is_superuser && <span className="user-badge staff">Staff</span>}
              </div>
              <button onClick={handleLogout} className="logout-button">Logout</button>
            </div>
          </div>
        </div>
        
        <p>Upload a campaign screenshot or PDF to extract all product info and save it to the database.</p>
        
        {!manualMode ? (
          <form onSubmit={handleSubmit} className="campaigns-form">
            <div className="file-upload-section">
              <label className="custom-file-label">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                {selectedFiles.length ? `${selectedFiles.length} file(s) selected` : 'Select Files'}
              </label>
              
              {selectedFiles.length > 0 && (
                <div className="selected-files">
                  <h4>Selected Files:</h4>
                  <ul className="file-list">
                    {selectedFiles.map((file, index) => (
                      <li key={index}>
                        <span className="file-info">
                          {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                        <button
                          type="button"
                          className="file-delete-btn"
                          onClick={() => handleFileDelete(index)}
                          title="Remove file"
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            <div className="button-row">
              <button type="submit" disabled={loading || !selectedFiles.length}>
                {loading ? 'Processing...' : 'Let AI work'}
              </button>
              <button 
                type="button" 
                onClick={handleManualMode}
                className="manual-button"
                title="Enter data manually"
              >
                Manual Handling
              </button>
            </div>
          </form>
        ) : (
          <div className="manual-mode-section">
            <div className="manual-header">
              <h3>Manual Data Entry</h3>
              <p>Enter campaign product data manually. All fields are optional except product name.</p>
            </div>
          </div>
        )}
        
        {error && (
          <div className="campaigns-error">
            {error}
            {canRetry && !loading && !manualMode && (
              <div className="error-actions">
                <button 
                  onClick={handleRetryExtraction}
                  className="error-retry-button"
                >
                  Try Again ({retryCount}/3)
                </button>
                <span className="retry-info">or use Manual Entry above</span>
              </div>
            )}
          </div>
        )}
        {saveStatus === 'success' && <div className="campaigns-success">{saveMessage}</div>}
        {saveStatus === 'error' && <div className="campaigns-error">Failed to save changes.</div>}
      </div>
      
      {manualMode && (
        <div className="campaigns-card">
          <h2>Manual Product Entry</h2>
          <div className="manual-controls">
            <button 
              onClick={handleAddRow}
              className="add-row-button"
            >
              ➕ Add Row
            </button>
            <span className="row-count">{manualProducts.length} rows</span>
          </div>
          
          <div className="campaigns-results">
            <div className="table-container">
              <table className="campaigns-table manual-table">
                <thead>
                  <tr>
                    <th>Actions</th>
                    {TABLE_HEADERS.map(h => (
                      <th key={h}>{h.replace(/_/g, ' ')}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {manualProducts.map((prod, rowIdx) => (
                    <tr key={rowIdx}>
                      <td className="actions-cell">
                        <button
                          onClick={() => handleRemoveRow(rowIdx)}
                          className="remove-row-button"
                          disabled={manualProducts.length <= 1}
                          title="Remove row"
                        >
                          🗑️
                        </button>
                      </td>
                      {TABLE_HEADERS.map((key, colIdx) => (
                        <td
                          key={colIdx}
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={e => handleManualCellChange(rowIdx, key, e.target.textContent)}
                          className={key === 'product_name' ? 'required-field' : ''}
                          data-placeholder={key === 'product_name' ? 'Product name (required)' : `Enter ${key.replace(/_/g, ' ')}`}
                        >
                          {prod[key] || ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="action-buttons">
              <button
                onClick={handleSaveManual}
                disabled={saveStatus === 'saving'}
                className="save-manual-button"
              >
                {saveStatus === 'saving' ? 'Saving...' : 'Save Manual Data'}
              </button>
              <button
                onClick={handleExitManual}
                className="secondary-button"
              >
                ← Back to File Upload
              </button>
            </div>
          </div>
        </div>
      )}
      
      {result && !manualMode && (
        <div className="campaigns-card">
          <h2>Extracted Campaign Data</h2>
          {editedProducts && editedProducts.length ? (
            <div className="campaigns-results">
              <div className="table-container">
                <table className="campaigns-table">
                  <thead>
                    <tr>
                      {TABLE_HEADERS.map(h => (
                        <th key={h}>{h.replace(/_/g, ' ')}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {editedProducts.map((prod, rowIdx) => (
                      <tr key={rowIdx}>
                        {TABLE_HEADERS.map((key, colIdx) => (
                          <td
                            key={colIdx}
                            contentEditable
                            suppressContentEditableWarning
                            onBlur={e => handleCellChange(rowIdx, key, e.target.textContent)}
                            style={{ background: '#fff' }}
                          >
                            {prod[key] || ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="action-buttons">
                <button
                  onClick={handleSave}
                  disabled={saveStatus === 'saving'}
                >
                  {saveStatus === 'saving' ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={handleRetry}
                  className="secondary-button"
                >
                  Upload More
                </button>
              </div>
            </div>
          ) : (
            <p>No products found.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default CampaignsPage; 